import { useMemo } from 'react';

import { isDemo } from '@dreryk/demo-variant-frontend';
import { useFormController, useTranslate } from '@dreryk/react-common';
import { CertificateInfo, CertificateUpload, FormContent, FormRouteLeavingPrompt } from '@dreryk/react-components';

import { PasswordField } from '../../common/fields/PasswordField';
import { UserCurrentEmailField } from '../../common/fields/UserCurrentEmailField';
import { StyledFormSectionHeader } from '../../common/StyledFormSectionHeader';
import { personnelTranslationKeys } from '../../translations';

import { useCertificateQuery } from './certificate.hooks';
import { CertificateFormBean, CertificateFormController } from './certificate.types';
import { CertificateSaveAction } from './CertificateSaveAction/CertificateSaveAction';

type CertificateFormProps = {
   readonly onCertificateSave: VoidFunction;
};
// eslint-disable-next-line max-lines-per-function
const CertificateForm = (props: CertificateFormProps): JSX.Element => {
   const { data: certificate, loading } = useCertificateQuery();
   const {
      handleSubmit,
      setError,
      register,
      formState,
   }: CertificateFormController = useFormController<CertificateFormBean>(
      {
         certificateFileDescriptor: null,
         certificatePassword: null,
         userPassword: null,
      },
      {
         resetAfterSubmit: true,
      }
   );
   const translate = useTranslate();
   const isDemoMode = isDemo();
   const fileUploadPlaceholder = isDemoMode
      ? personnelTranslationKeys.myAccount.certificate.unavailableInDemo
      : personnelTranslationKeys.myAccount.certificate.fileUploadPlaceholder;

   const certificateUploadValidationMessages = useMemo(
      () => ({
         fileRequired: personnelTranslationKeys.myAccount.certificate.form.chooseCertificateFile,
      }),
      []
   );

   return (
      <>
         <form noValidate autoComplete={'off'}>
            <FormContent testId={'certificate'}>
               <FormContent testId={'currentCertificate'}>
                  <StyledFormSectionHeader>
                     {translate(personnelTranslationKeys.myAccount.certificate.form.current)}
                  </StyledFormSectionHeader>

                  <CertificateInfo certificate={certificate!} isLoading={loading} testId={'certificateInfo'} />
               </FormContent>
               <FormContent testId={'changeCertificate'}>
                  <StyledFormSectionHeader>
                     {translate(personnelTranslationKeys.myAccount.certificate.form.change)}
                  </StyledFormSectionHeader>
                  <CertificateUpload
                     certificateLoading={loading}
                     fileController={register('certificateFileDescriptor')}
                     passwordController={register('certificatePassword')}
                     fileUploadLabel={personnelTranslationKeys.myAccount.certificate.form.choose}
                     validationMessages={certificateUploadValidationMessages}
                     fileUploadPlaceholder={fileUploadPlaceholder}
                     uploadDisabled={isDemoMode}
                     testId={'certificateUpload'}
                  />
               </FormContent>
               <FormContent testId={'authentication'}>
                  <StyledFormSectionHeader>
                     {translate(personnelTranslationKeys.myAccount.certificate.form.authenticate)}
                  </StyledFormSectionHeader>
                  <FormContent testId={'authentication'}>
                     <UserCurrentEmailField
                        label={personnelTranslationKeys.myAccount.certificate.form.email}
                        controller={register('email')}
                     />
                     <PasswordField
                        label={personnelTranslationKeys.myAccount.certificate.form.password}
                        controller={register('userPassword', { required: true })}
                        disabled={loading}
                        testId={'userPassword'}
                     />
                  </FormContent>
               </FormContent>
            </FormContent>
         </form>

         <CertificateSaveAction handleSubmit={handleSubmit} onSuccess={props.onCertificateSave} setError={setError} />
         <FormRouteLeavingPrompt
            formState={formState}
            title={personnelTranslationKeys.myAccount.certificate.form.leavingPrompt.title}
            message={personnelTranslationKeys.myAccount.certificate.form.leavingPrompt.message}
         />
      </>
   );
};

export { CertificateForm };
